"use client";

import { useState } from "react";
import { Plus, Cpu, Layers, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const [activeStage, setActiveStage] = useState(0);
  const stages = [{
    number: "01",
    title: "NETWORK ANALYSIS",
    description: "We audit your connection for latency, packet loss, and jitter. Every hop, every route, every server node is verified against gaming standards before optimization begins.",
    metric: " 24hrs audit cycle",
    icon: Cpu,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_network_analysis.png",
    diagramAlt: "Network analysis diagram showing gaming traffic flow"
  }, {
    number: "02",
    title: "ROUTE OPTIMIZATION",
    description: "Multi-path traffic routing on premium low-latency nodes. In-house server switching and direct peering deliver pro-grade connection speeds in seconds, not minutes.",
    metric: " 48hrs setup time",
    icon: Layers,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_route_optimization.png",
    diagramAlt: "Traffic routing technical diagram"
  }, {
    number: "03",
    title: "LATENCY REDUCTION",
    description: "Precision packet prioritization down to individual game ports, paired with server profiles tuned to your ISP. Each session is actively tested against your ping goals.",
    metric: " 0.05ms jitter reduction",
    icon: Wrench,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_latency_reduction.webp",
    diagramAlt: "Latency and packet prioritization diagram"
  }, {
    number: "04",
    title: "STABILITY CHECK",
    description: "Comprehensive connection validation: packet stability, server load balancing, ping consistency, and ISP throttling checks. Documentation packaged for your gaming setup.",
    metric: " 99.9% Uptime Class",
    icon: CheckCircle2,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_stability_check.png",
    diagramAlt: "Stability and connection testing diagram"
  }];
  const certifications = ["LOW-LATENCY", "SECURE", "ISO 27001"];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const activeDiagram = stages[activeStage];
  return <section data-section-id="4628" id="gamingtrafficoptimization" className="bg-background text-foreground py-24 md:py-40 px-4 sm:px-6 lg:px-10 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      {}
      <div className="absolute top-6 left-6 font-mono text-[10px] text-muted-foreground tracking-widest hidden md:block">
        [X: 0000 / Y: 0000]
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] text-muted-foreground tracking-widest hidden md:block">
        REV.04
      </div>

      <div className="max-w-7xl mx-auto relative">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="text-center mb-10 md:mb-14">
            <span className="font-mono text-xs md:text-sm text-primary tracking-[0.25em] inline-flex items-center gap-2">
              <span className="w-8 h-px bg-primary" />
              {"// SECTION_03 — OPTIMIZATION"}
              <span className="w-8 h-px bg-primary" />
            </span>
          </div>

          <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95] max-w-5xl">
            Four steps.
            <br />
            <span className="text-muted-foreground">Zero lag spikes.</span>
          </h2>

          <div className="mt-8 flex items-center gap-4 font-mono text-xs text-muted-foreground">
            <span className="w-12 h-px bg-border" />
            <span>END-TO-END GAMING TRAFFIC WORKFLOW</span>
          </div>
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 transition-all duration-700 delay-150 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-3 order-1">
            <div className="bg-card border border-primary/20 rounded-md relative aspect-square lg:aspect-auto lg:h-full min-h-[400px] overflow-hidden">
              {}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 border-b border-border/60 bg-background/40 backdrop-blur-sm z-10">
                <span className="font-mono text-[10px] text-primary tracking-widest">
                  DIAGRAM_{activeDiagram.number}.SVG
                </span>
                <span className="font-mono text-[10px] text-muted-foreground tracking-widest">
                  {activeDiagram.title}
                </span>
              </div>

              {}
              <div className="absolute top-12 left-4 w-3 h-3 border-l border-t border-primary/40" />
              <div className="absolute top-12 right-4 w-3 h-3 border-r border-t border-primary/40" />
              <div className="absolute bottom-12 left-4 w-3 h-3 border-l border-b border-primary/40" />
              <div className="absolute bottom-12 right-4 w-3 h-3 border-r border-b border-primary/40" />

              {}
              <div className="absolute inset-0 flex items-center justify-center p-8 pt-16 pb-16">
                <img key={activeDiagram.diagram} src={activeDiagram.diagram} data-wvc-srcset={`${activeDiagram.diagram}-wvc-srcset`} data-wvc-sizes={`${activeDiagram.diagram}-wvc-sizes`} alt={activeDiagram.diagramAlt} loading="lazy" decoding="async" className="w-full h-full object-contain opacity-90 transition-opacity duration-500" />
              </div>

              {}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-3 border-t border-border/60 bg-background/40 backdrop-blur-sm">
                <span className="font-mono text-[10px] text-muted-foreground tracking-widest">
                  STAGE {activeDiagram.number} / 04
                </span>
                <span className="font-mono text-[10px] text-primary tracking-widest">
                  {activeDiagram.metric}
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-2 order-2 relative">
            {}
            <div className="absolute left-3 top-6 bottom-6 w-px bg-border hidden md:block" />

            <div className="flex flex-col">
              {stages.map((stage, i) => {
              const isActive = activeStage === i;
              const Icon = stage.icon;
              return <div key={stage.number} data-index={i} className={`relative border-b border-border last:border-b-0 transition-all duration-300 ${isActive ? "border-l-[3px] border-l-primary pl-5" : "border-l-[3px] border-l-transparent pl-5"}`}>
                    {}
                    <div className={`absolute -left-[7px] top-7 w-3 h-3 rounded-md border transition-all duration-300 hidden md:block ${isActive ? "bg-primary border-primary shadow-[0_0_12px_var(--primary)]" : "bg-background border-border"}`} />

                    <button onClick={() => setActiveStage(i)} className="w-full text-left py-6 flex items-start justify-between gap-4 group cursor-pointer" aria-expanded={isActive}>
                      <div className="flex items-baseline gap-4 flex-1 min-w-0">
                        <span className={`font-mono text-sm tracking-widest shrink-0 transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                          {stage.number} /
                        </span>
                        <h3 className={`font-default text-xl md:text-2xl font-semibold tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/70 group-hover:text-foreground"}`}>
                          {stage.title}
                        </h3>
                      </div>
                      <Plus className={`size-4 shrink-0 mt-2 transition-all duration-300 ${isActive ? "rotate-45 text-primary" : "text-muted-foreground"}`} />
                    </button>

                    {}
                    <div className={`grid transition-all duration-500 ease-in-out ${isActive ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="pl-0 md:pl-[3.25rem] space-y-4">
                          <p className="text-sm text-muted-foreground leading-relaxed font-default">
                            {stage.description}
                          </p>
                          <div className="flex items-center gap-3 pt-2">
                            <Icon className="size-4 text-primary" />
                            <span className="font-mono text-xs text-primary tracking-wider">
                              {stage.metric}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <span className="font-mono text-[10px] text-muted-foreground tracking-widest">
              {"// COMPLIANCE & STANDARDS"}
            </span>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              {certifications.map((cert, i) => <span key={cert} data-index={i} className="font-mono text-xs text-foreground tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary rounded-md" />
                  {cert}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}